/**
 * Геометрия строчных видов `list`/`compact`: типы каркаса, константы высот и
 * sx-хелперы вместе с их обоснованиями. Выделено из `ListRowFrame.tsx`, который
 * упёрся в лимит длины файла (350 строк) — разрез «геометрия отдельно от
 * разметки» не делит ни одного ветвления и ничего не меняет в поведении.
 * Публичные имена каркаса (`ROW_CONTAINER`, `buildRowQuery`, `WIDE_ROW`,
 * `getRowSurfaceSx` и типы) ре-экспортируются из `ListRowFrame.tsx`, поэтому
 * домены продолжают импортировать их оттуда.
 */
import { ReactNode } from 'react';

import { SxProps, Theme } from '@mui/material';
import { blueGrey } from '@mui/material/colors';

import {
  FIELD_LABEL_DENSE_SX,
  FIELD_VALUE_DENSE_SX
} from '@/shared/ui/styles/fieldTypography';

/** Развёрнутый вид (три подписанных поля) или компактный (одна строка через «·») */
export type TListRowVariant = 'list' | 'compact';

/** Выключка угла `trailing` по вертикали: у верхней кромки (дефолт) или по центру строки */
export type TListRowTrailingAlign = 'top' | 'center';

/** Пропорция медиа-слота: фото человека (3:4) или постер проекта (2:3, 19243) */
export type TListRowMediaAspect = '3/4' | '2/3' | '1/1';

/**
 * Выключка медиа-слота по кросс-оси строки: `center` (дефолт) держит фото по
 * центру строки — верно для портретного медиа, где высота слота и высота
 * блока имя+поля сопоставимы. `flex-start` прижимает слот к верхней кромке —
 * нужен обложке проекта (18976, волна 2, пункт 3): без него альбомная
 * обложка рисуется по центру уже подросшей строки (клэмп названия на 2
 * строки, три поля) и читается как случайно смещённая вниз, а не как
 * заголовок карточки.
 */
export type TListRowMediaAlign = 'center' | 'flex-start';

/**
 * Поле строки `list`. Значение печатается как есть; пустое (`''`/`null`/`undefined`)
 * каркас сам заменяет приглушённым «Не указано» — поле остаётся на месте, а не
 * исчезает, иначе соседние строки списка расходятся по высоте (18170).
 */
export interface IRowField {
  label: string;
  value: ReactNode;
  /**
   * Форма пустого значения; дефолт — «Не указано» (средний род). Не все подписи
   * среднего рода: «Возраст», «Город», «Тип проекта», «Прием заявок», «Гонорар»,
   * «Режиссёр», «Период», «Статус» — мужского («Не указан»), «Роль» — женского
   * («Не указана»). Каркас не разбирает подпись, чтобы угадать род, — домен,
   * которому подпись известна буквально, объявляет форму сам (18976, Р6).
   */
  emptyValue?: string;
}

/**
 * Имя контейнера строчных видов — ОДНО на все домены (актёр, визитка, кастинг,
 * проект, 18976). До каркаса у каждого домена было своё имя со своим порогом
 * (`user-card-row`, `actor-card-row`, `casting-row`, `project-row`) и своим
 * порогом «развернуть стопку полей в одну линию»; общий каркас оставляет ИМЯ и
 * ПОРОГ актёрской строки, потому что только она уже держала поля колонкой без
 * разворачивания — остальные три домена переезжают на эту геометрию.
 */
export const ROW_CONTAINER = 'user-card-row';

/**
 * Строит контейнерный запрос строчных видов на ПРОИЗВОЛЬНОМ пороге ширины.
 * Каркас объявляет ОДНО имя контейнера (`ROW_CONTAINER`) — единственный примитив
 * порога, который он обязан отдавать домену; сам порог принадлежит домену и у
 * каждого свой (актёр и визитка — 420px, кастинг и проект — 560px: у последних
 * двух в ряду больше подписанных значений, и они разворачиваются позже). Домен
 * строит свой порог этим билдером, а не подставляет чужой готовый порог вроде
 * `WIDE_ROW` — тот принадлежит внутренней геометрии каркаса, см. его JSDoc.
 * @param minWidth - ширина контейнера в пикселях, с которой начинается «широкая» ветка
 */
export const buildRowQuery = (minWidth: number): string =>
  `@container ${ROW_CONTAINER} (min-width: ${minWidth}px)`;

/**
 * ВНУТРЕННИЙ порог геометрии самого `ListRowFrame`: 420px — граница, на которой
 * растёт подпись поля (12 → 14px) и паддинг корня (18976, Р71 — медиа-слот
 * `list` растить на этом пороге ПЕРЕСТАЛИ, см. `LIST_MEDIA_HEIGHT`). Это НЕ
 * примитив для доменных порогов — домены, переезжающие на каркас, обязаны
 * строить СВОЙ порог через `buildRowQuery(...)`, а не подставлять этот `WIDE_ROW`
 * вместо своего. У актёра и визитки их порог сейчас ЧИСЛЕННО совпадает с этим
 * (420px) — тем более важно не сокращать до `WIDE_ROW`: правка внутренней
 * геометрии каркаса тогда молча уедет в доменную разметку, не показав поломки.
 */
export const WIDE_ROW = buildRowQuery(420);

/**
 * Высота медиа-слота `list` — ОДНА на любой ширине (18976, Р71). Первый заход
 * растил её 112 → 144px на широкой ветке; замер и кадр реестра показали, что
 * высоту строки при этом стало диктовать ФОТО, а не содержимое (две строки
 * текста ≈60px против медиа 144px) — на широком экране, где горизонтали
 * БОЛЬШЕ, строка становилась ВЫШЕ, прямо против цели прохода («строка ниже, а
 * не шире»). Решение заказчика 17.09.2026: рост убрать, 112px везде. Постер
 * проекта (`2/3`, `LIST_MEDIA_HEIGHT_POSTER`) свой рост и не получает.
 */
export const LIST_MEDIA_HEIGHT = 112;
export const COMPACT_MEDIA_HEIGHT = 48;
export const LIST_MIN_HEIGHT = 136;
export const COMPACT_MIN_HEIGHT = 64;

/**
 * Высота постера проекта (`2/3`) в строке `list` — 84px, слот 56×84 (19243).
 * НИЖЕ `LIST_MEDIA_HEIGHT` фото человека (112px): строка проекта несёт
 * название в две строки и три поля, и постер 75×112 перерастал бы текст,
 * диктуя высоту строки картинкой. 84px — прежняя высота горизонтальной
 * обложки (`3/2`, 18976, волна 2), поэтому высота строки проекта не меняется,
 * а постер забирает у названия на 70px меньше ширины, чем прежние 126px.
 */
export const LIST_MEDIA_HEIGHT_POSTER = 84;

/**
 * Высота слота логотипа (`1/1`) — 56px, слот 56×56: та же ширина, что у
 * постера 56×84 (карточка сущности в уведомлении, 19633)
 */
export const LIST_MEDIA_HEIGHT_LOGO = 56;

/**
 * Высота узкого фото человека (`3/4`, опт-ин `mediaNarrow`) — 75px, слот 56×75:
 * ширина как у постера и логотипа. Обычное фото остаётся 84×112
 */
export const LIST_MEDIA_HEIGHT_NARROW = 75;

export const ROW_LABEL_SX: SxProps<Theme> = {
  ...FIELD_LABEL_DENSE_SX,
  lineHeight: 1.33,
  flexShrink: 0,
  [WIDE_ROW]: { fontSize: '14px', lineHeight: 1.43 }
};

export const ROW_VALUE_SX: SxProps<Theme> = {
  ...FIELD_VALUE_DENSE_SX,
  [WIDE_ROW]: { fontSize: '14px' }
};

export const ROW_VALUE_EMPTY_SX: SxProps<Theme> = {
  ...ROW_VALUE_SX,
  color: 'text.disabled'
};

/**
 * Плотный ритм текстового блока (`tight`, карточки уведомлений, 19633; макет:
 * `.plate-title` 1.3, `.plate-meta` mt 2 / 1.45, `.plate-fields` mt 6 / 1.6).
 * Зазоры между слотами — только собственные отступы слотов, а не `gap: 1`;
 * название — первый ребёнок блока (сам или обёртка `titleActions`)
 */
export const TIGHT_BODY_SX: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  gap: 0,
  '& > :first-child, & > :first-child .MuiTypography-root': {
    lineHeight: 1.3
  }
};

/**
 * Подзаголовок: обычный, приглушённый или плотный (`tight`: mt 2px, 1.45 у ребёнка)
 * @param muted - приглушённый цвет (`text.disabled`)
 * @param tight - плотный ритм карточки уведомления
 */
export const getSubtitleSx = (muted = false, tight = false): SxProps<Theme> => {
  const base = muted ? ROW_VALUE_EMPTY_SX : ROW_VALUE_SX;

  return tight
    ? { ...base, mt: '2px', lineHeight: 1.45, '& > *': { lineHeight: 1.45 } }
    : base;
};

/**
 * Ряд полей в плотном ритме (`tight`): отступ сверху 6px на любой ширине, без
 * зазоров между строками полей, интерлиньяж подписи и значения 1.6
 */
export const TIGHT_FIELDS_SX: SxProps<Theme> = {
  gap: 0,
  rowGap: 0,
  mt: '6px',
  [WIDE_ROW]: { rowGap: 0, mt: '6px' },
  '& .MuiTypography-root': { lineHeight: 1.6 }
};

/**
 * Плотные поля парой в строку (`tight` + `fieldsInline`, карточки уведомлений):
 * таблица в две колонки — подписи слева по ширине самой длинной, значения с
 * одного края. Пары подряд рисовали значение сразу за своей подписью, и при
 * разной длине подписей («Режиссёр»/«Период») значения начинались вразнобой.
 * Каждое поле — своя строка на любой ширине; пара — `display: contents`
 * (`TIGHT_FIELD_INLINE_SX`), её подпись и значение — ячейки этой таблицы
 */
export const TIGHT_FIELDS_GRID_SX: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'max-content minmax(0, 1fr)',
  columnGap: 2,
  alignItems: 'baseline',
  [WIDE_ROW]: { columnGap: 2 }
};

/** Пара `TIGHT_FIELDS_GRID_SX`: своего бокса нет, подпись и значение — ячейки таблицы */
export const TIGHT_FIELD_INLINE_SX: SxProps<Theme> = {
  display: 'contents',
  '& > :last-child': { overflowWrap: 'break-word' }
};

/**
 * Поверхность строки — фон, рамка, радиус и подсветка наведения. ДО каркаса
 * каждый из четырёх доменов (актёр, визитка, кастинг, проект) держал свою копию
 * (или не держал вовсе): актёрская строка одна оборачивала себя в `<Card>`, три
 * остальных возвращали голый `Box` без поверхности вовсе, и на фоне страницы
 * (`palette.custom.background`) «бумага» строки у них пропадала. Каркас уже
 * владеет высотами, паддингами и типографикой строки — поверхность той же
 * природы, поэтому она здесь одна, а не в четырёх копиях. Значения — те же, что
 * нёс `<Card>` (`shared/ui/mui/card.ts`: `borderRadius: shape.borderL`,
 * `1px solid blueGrey[50]`, тень темы `shadows[0]` — у неё всегда `none`).
 *
 * Наведение поднимает `shadows[6]` — тот же приём, что у `BaseProjectCard`,
 * `BaseCastingCard` и `BaseParticipantCard` (18976, Р5). Серая заливка
 * (`backgroundColor: 'action.hover'`) — прежний приём именно этой поверхности —
 * была ей чужеродна: заливкой в проекте отмечают интерактивные элементы (меню,
 * вкладки, кнопки), а не карточки контента.
 *
 * `isInteractive` (18976, Р67, следствие отмены Р68): ховер-тень обещает
 * клик, поэтому строка БЕЗ клика (реестр визиток — карточки некликабельны,
 * редактирование только через кебаб) не должна её получать. Дефолт `true` —
 * ни один из трёх кликабельных доменов (актёр, кастинг, проект, все под
 * `<Link>`) не обязан передавать флаг сам, только реестр визиток передаёт
 * `false`.
 * @param theme - тема MUI
 * @param isInteractive - ведёт ли клик по строке куда-то; по умолчанию `true`
 */
export const getRowSurfaceSx = (
  theme: Theme,
  isInteractive = true
): SxProps<Theme> => ({
  backgroundColor: 'background.paper',
  borderRadius: theme.shape.borderL,
  border: '1px solid',
  borderColor: blueGrey[50],
  ...(isInteractive ? { ':hover': { boxShadow: theme.shadows[6] } } : {})
});

/**
 * Стили корня строки под вариант. `containerType`/`containerName` ЗДЕСЬ не
 * объявляются (18976, Р66) — контейнер несёт `ROW_CONTAINER_SX` на обёртке
 * выше (см. её JSDoc): элемент не может разрешить `@container` по
 * СОБСТВЕННОМУ объявлению контейнмента, а именно этот корень раньше был
 * одновременно и контейнером, и получателем `[WIDE_ROW]`-паддинга ниже —
 * самоссылка, которую спецификация CSS игнорирует. Паддинг остаётся здесь,
 * потому что теперь это стиль ПОТОМКА контейнера, а не его самого.
 * @param variant - `list` или `compact`
 */
/**
 * Высота `compact` с парой решения (`decisionSlot`, 18976, Р73, пятый заход) —
 * пара переехала в АБСОЛЮТНЫЙ нижний правый угол (см. `getDecisionCornerSx`),
 * зеркальный верхнему (`trailing`, `getCornerSx`), и, в отличие от третьего
 * захода (пара в потоке `RowBody`, растила строку сама), больше не занимает
 * поток — `COMPACT_MIN_HEIGHT` её высоту не увидит вовсе. Число подобрано так,
 * чтобы оба угла (по 44px тач-цели плюс инсет `theme.spacing(2)` с обеих
 * сторон) не пересекались с зазором: `8 + 44 + 8(зазор) + 44 + 8 = 112`.
 */
export const COMPACT_DECISION_MIN_HEIGHT = 112;

/**
 * `compact` с парой решения у домена с порогом головы (`compactHeadWrap`): уже
 * порога `meta` стоит второй строкой во всю ширину, и абсолютная пара
 * (`getDecisionCornerSx`) ложилась поверх неё — на 375 роль кастинга пряталась
 * под «Отклонить» (фикс-волна 4 19243). Здесь пара получает свою полосу внизу:
 * нижний паддинг = инсет угла + тач-цель 44 + зазор, текст прижат к верху. От
 * порога голова в одну строку, и пара снова встаёт в угол рядом с ней
 * @param query - порог головы домена
 */
export const getCompactDecisionLaneSx = (query: string) => ({
  alignItems: 'flex-start',
  /**
   * Инсет угла и зазор над парой — по `theme.spacing(2)`, плюс тач-цель 44
   * @param theme - тема MUI
   */
  paddingBottom: (theme: Theme) => `calc(${theme.spacing(4)} + 44px)`,
  [query]: { alignItems: 'center', paddingBottom: 2 }
});

/**
 * Полоса пары решения для любой `compact`-строки с парой. Домен с порогом головы
 * (`compactHeadWrap`, кастинг) получает полосу уже порога. Домен без порога
 * (актёр в откликах режиссёра, `CardListRow`) — ниже `md` (900): там кнопки
 * пары растут до тач-цели 44px (`minHeight` кнопки решения уже `md`), и угловая
 * пара заходила на аватар и строку имени (финальный мини-фикс 19243, замер:
 * 375 и 494 — 20px по аватару и 4–8px по имени, 760 — 6–8px по городу и
 * кастингу). От `md` кнопки 34px и встают под строкой имени без пересечения —
 * угол прежний. Порог — тот же брейкпоинт, что у высоты кнопок, а не ширина
 * строки: на 494 строка 455px пересекалась, на 900 строка 450px — нет
 * @param hasDecision - есть ли у строки пара решения в нижнем углу
 * @param compactHeadWrap - порог головы домена, если он есть
 */
export const getDecisionLaneSx = (
  hasDecision: boolean,
  compactHeadWrap?: string
) => {
  if (!hasDecision) return false;
  if (compactHeadWrap) return getCompactDecisionLaneSx(compactHeadWrap);

  return (theme: Theme) => getCompactDecisionLaneSx(theme.breakpoints.up('md'));
};

/** Что о медиа-слоте нужно корню строки, чтобы выбрать выключку и нижнюю планку высоты */
export interface IRootMediaParams {
  media?: ReactNode;
  aspect: TListRowMediaAspect;
  align: TListRowMediaAlign;
  /** Узкое фото 56×75 вместо 84×112 (только `3/4`) */
  narrow?: boolean;
}

/**
 * Нижняя планка высоты `list`. `LIST_MIN_HEIGHT` (136) выведен из ПОРТРЕТНОГО
 * медиа — `LIST_MEDIA_HEIGHT` 112 плюс паддинг 12 сверху и снизу — и потому
 * держится ТОЛЬКО у строки с портретным медиа (актёр, визитка). Постеру
 * проекта (`2/3`, 84px) та же планка оставляла ~28px пустоты, а прижатая к
 * верху обложка (`flex-start`) отдавала их все НИЖНЕМУ отступу: 12px сверху
 * против 40px снизу (19061). Строка БЕЗ медиа (кастинг) получала то же самое:
 * текст с трейлингом прижат к верху, а планка, рассчитанная на фото, которого
 * нет, растила пустоту под «Роль / Прием заявок / Гонорар» (19061). У обеих планки
 * нет — высоту даёт обложка или текст плюс паддинг, отступы сверху и снизу
 * равны.
 * @param hasDecision - `compact`: есть ли пара решения (см. `COMPACT_DECISION_MIN_HEIGHT`)
 * @param variant - `list` или `compact`
 * @param media - наличие и пропорция медиа-слота
 */
const getRootMinHeight = (
  hasDecision: boolean,
  variant: TListRowVariant,
  media: IRootMediaParams
): number | 'auto' => {
  if (variant === 'compact') {
    return hasDecision ? COMPACT_DECISION_MIN_HEIGHT : COMPACT_MIN_HEIGHT;
  }

  return media.media && media.aspect === '3/4' && !media.narrow
    ? LIST_MIN_HEIGHT
    : 'auto';
};

/**
 * @param variant - `list` или `compact`
 * @param hasDecision - `compact`: есть ли у строки `decisionSlot` — бампает
 * минимальную высоту, чтобы оба угла (иконки сверху, пара решения снизу) не
 * пересекались (см. `COMPACT_DECISION_MIN_HEIGHT`). `list` не читает флаг —
 * там `decisionSlot` в потоке и растит строку сам.
 * @param media - медиа-слот строки: наличие, пропорция и выключка. Выключка
 * `list` — общая для медиа И текстового блока: обложка, прижатая к верху
 * (`flex-start`, проект), тянет к верху и название с полями — иначе текст
 * центрировался по высоте строки и «висел» ниже верхней кромки обложки
 * (19061). `compact` всегда центрирует — голова в одну строку.
 */
export const getRootSx = (
  variant: TListRowVariant,
  hasDecision: boolean,
  media: IRootMediaParams = { aspect: '3/4', align: 'center' }
): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: variant === 'list' ? media.align : 'center',
  width: '100%',
  gap: variant === 'compact' ? 2 : 3,
  padding: 2,
  minHeight: getRootMinHeight(hasDecision, variant, media),
  [WIDE_ROW]: variant === 'list' ? { padding: 3 } : {}
});

/**
 * Высота медиа-слота `list` по пропорции
 * @param aspect - пропорция медиа
 * @param narrow - узкое фото 56×75 (опт-ин, только `3/4`)
 */
const getListMediaHeight = (aspect: TListRowMediaAspect, narrow: boolean) => {
  if (aspect === '2/3') {
    return LIST_MEDIA_HEIGHT_POSTER;
  }
  if (aspect === '1/1') {
    return LIST_MEDIA_HEIGHT_LOGO;
  }

  return narrow ? LIST_MEDIA_HEIGHT_NARROW : LIST_MEDIA_HEIGHT;
};

/**
 * Стили медиа-слота под вариант и пропорцию. Высоту задаёт каркас — содержимое
 * заполняет её (`width/height: 100%`), домен собственную высоту не носит.
 * Постер (`2/3`) получает свою, более низкую базовую высоту
 * (`LIST_MEDIA_HEIGHT_POSTER`) — см. её JSDoc. Высота НЕ растёт на широкой
 * строке ни у одной пропорции (18976, Р71).
 * @param variant - `list` или `compact`
 * @param aspect - пропорция медиа
 * @param align - выключка слота по кросс-оси строки (см. `IListRowFrameProps.mediaAlign`)
 * @param narrow - узкое фото 56×75 вместо 84×112 (опт-ин)
 */
export const getMediaBoxSx = (
  variant: TListRowVariant,
  aspect: TListRowMediaAspect,
  align: TListRowMediaAlign,
  narrow = false
): SxProps<Theme> => ({
  width: 'auto',
  height:
    variant === 'compact'
      ? COMPACT_MEDIA_HEIGHT
      : getListMediaHeight(aspect, narrow),
  aspectRatio: aspect,
  flexShrink: 0,
  alignSelf: align,
  display: 'flex'
});

/** Заполняет весь медиа-слот, размер которого задаёт каркас */
export const MEDIA_FILL_SX: SxProps<Theme> = { width: '100%', height: '100%' };

/**
 * Пусто ли значение поля — печатается ли вместо него «Не указано». Проверяются
 * только примитивы: готовую разметку (чип, ссылку) каркас не трогает.
 * @param value - значение поля
 */
export const isFieldEmpty = (value: ReactNode): boolean =>
  value === '' || value === null || value === undefined;

/**
 * Пара «подпись → значение»: подпись НАД значением ниже `WIDE_ROW`, в одну
 * линию — на ней и выше (18976, Р2).
 *
 * Корень трёх четвертей дефектов приёмки: на узкой строке одновременно живут
 * медиа, трейлинг и инлайновая пара «подпись: значение» — тексту остаётся
 * ~130px, и «Не указано»/период рвутся на несколько строк непредсказуемо
 * (третья строка рисуется поверх соседних полей). Стопка убирает перенос:
 * подписи однословные и короткие, значению отдаётся вся ширина текстовой
 * колонки, а не половина инлайновой пары.
 *
 * `alignItems: 'center'`, а не `'baseline'` (18976, Р72.1): пилюля статуса не
 * несёт текстовую базовую линию — браузер синтезирует ей baseline по НИЖНЕМУ
 * краю бокса, и при выравнивании подписи по этой линии подпись пилюльной пары
 * съезжала вниз относительно подписей текстовых пар той же строки (замер
 * `getBoundingClientRect` подписей: разброс top ~5.7px на `/actor-registry`,
 * 1900px). `center` не зависит от того, есть ли у значения текстовая базовая
 * линия — подпись и значение центрируются по высоте пары одинаково что для
 * текста, что для чипа, и подписи всех полей строки остаются на одной линии.
 */
export const FRAME_FIELD_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  [WIDE_ROW]: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    // Пара не сжимается флексом при нехватке места — вместо усечения текста
    // ВЕСЬ элемент уходит на следующую строку (см. `flexWrap` контейнера,
    // `LIST_FIELDS_SX`); без этого браузер сначала пытается ужать пары в
    // текущей строке и только потом переносит.
    flexShrink: 0
  }
};

/**
 * Пара «подпись значение» ОДНОЙ строкой и ниже `WIDE_ROW` (`fieldsInline`, проект,
 * макет v2 §2: «одно поле — одна строка»). Значение берёт
 * остаток строки после подписи (`flex: 1 1 0`, `minWidth: 0`): длинное значение (несколько
 * режиссёров) переносится СПРАВА от подписи, в своей колонке, а не уходит под
 * подпись целиком — перенос пары (`flexWrap`) ронял всё значение на новую строку.
 * На широкой строке — как `FRAME_FIELD_SX`: пара не сжимается и по ширине своего
 * содержимого (значение `0 1 auto`), при нехватке места переносится весь элемент.
 * Пара, которая шире всей строки и одна (длинный список режиссёров), упирается в
 * `maxWidth: 100%`, и значение переносится справа от подписи, а не вылезает за
 * край карточки (19633: в уведомлении строка уходила за рамку). Выключка по
 * базовой линии и на широкой строке: подпись стоит у первой строки переноса, а
 * не посередине между строками.
 * Стопка `FRAME_FIELD_SX` у остальных доменов не меняется (18976, Р2)
 */
export const FRAME_FIELD_INLINE_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'baseline',
  columnGap: 2,
  '& > :last-child': { flex: '1 1 0', minWidth: 0 },
  [WIDE_ROW]: {
    gap: 2,
    flexShrink: 0,
    maxWidth: '100%',
    '& > :last-child': { flex: '0 1 auto' }
  }
};

/**
 * Контейнер трёх полей `list` (18976, Р55, после разбраковки заказчиком
 * 17.09.2026 двух РАВНЫХ колонок CSS Grid на всю ширину строки — та версия
 * давала не «поля в ряд», а поля вразнобой: одна пара слева, другая посреди
 * экрана, между ними пропасть в треть ширины строки, а высота при этом не
 * падала). Верная цель — раскладка прежнего стейджа: три пары «подпись:
 * значение» одной ГОРИЗОНТАЛЬНОЙ строкой слева, ширина каждой — по
 * содержимому, БЕЗ растягивания на ширину строки; вся освободившаяся ширина
 * достаётся паре решения (`decisionSlot`, см. её JSDoc), когда она есть, а не
 * делится между полями поровну.
 *
 * Ниже широкого порога — прежняя колонка (одно поле под другим, три строки).
 * На широком — `display: flex; flex-wrap: wrap`, а не `grid`: пары не делят
 * между собой готовые дорожки сетки, а просто идут одна за другой слева,
 * перенос на вторую строку — только когда очередная пара физически не
 * помещается (`flexShrink: 0` у самой пары, см. `FRAME_FIELD_SX`), а не по
 * жёсткому «после второй — новая строка» правилу CSS Grid.
 */
export const LIST_FIELDS_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  mt: 1,
  [WIDE_ROW]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 4,
    rowGap: 1,
    mt: 0
  }
};

/**
 * Верхний правый угол строки — ФИКСИРОВАННОЕ место `trailing` (папка + звезда,
 * статусные иконки рядом с ней, кебаб — 18976, Р73, пятый заход: заказчик
 * прямо потребовал, чтобы зазор угла до края СОВПАДАЛ с собственным паддингом
 * корня строки (`getRootSx`), «я вижу что это 8px, кажется так лучше будет
 * смотреться») — а не нёс отдельно подобранное число.
 *
 * Инсет — `theme.spacing(2)` (база) / `theme.spacing(3)` (`WIDE_ROW`, ТОЛЬКО
 * `list` — `compact` паддинг корня не растёт, см. `getRootSx`), буквально ТЕ
 * ЖЕ вызовы `theme.spacing`, что несёт паддинг `getRootSx`: числа совпадают по
 * построению, а не по совпадению — раньше здесь стояли сырые пиксели (16/24),
 * и при спейсинге темы 4 (`ThemeProvider.tsx`) это было РОВНО ВДВОЕ больше
 * настоящего паддинга (8/12) — замер поймал разрыв (`V-overlap-fix.md`).
 *
 * Абсолютная позиция читает расстояние до края от размера самой строки
 * (`ROW_CONTAINER_SX`, единственный `position: relative` предок ниже) — не
 * зависит от того, что ещё стоит в строке и есть ли у неё `decisionSlot`.
 *
 * Группа не занимает поток — домен обязан зарезервировать под неё место в
 * `titleActions` (см. её JSDoc в `IListRowFrameProps`), иначе заголовок наедет
 * на неё текстом (тот же приём и та же причина, что у сердца/папки актёрской
 * строки, см. `UserCardCornerActions`/`getActionsReserveSx` в `CardListRow`).
 * @param theme - тема MUI (для `theme.spacing`, той же шкалы, что несёт паддинг `getRootSx`)
 * @param variant - `list` или `compact`
 * @param align - 'top' — верхний угол на паддинге корня (дефолт); 'center' — середина строки по вертикали
 * (проект compact, макет v2 §4: «⋮» прижатый к верху двухстрочной строки читался как оторванный)
 */
export const getCornerSx = (
  theme: Theme,
  variant: TListRowVariant,
  align: TListRowTrailingAlign = 'top'
): SxProps<Theme> => {
  const base = theme.spacing(2);

  if (align === 'center') {
    return {
      position: 'absolute',
      zIndex: 2,
      top: '50%',
      right: base,
      transform: 'translateY(-50%)'
    };
  }

  return {
    position: 'absolute',
    zIndex: 2,
    top: base,
    right: base,
    ...(variant === 'list'
      ? { [WIDE_ROW]: { top: theme.spacing(3), right: theme.spacing(3) } }
      : {})
  };
};

/**
 * Нижний правый угол `compact` — ЗЕРКАЛЬНЫЙ `getCornerSx` угол ДЛЯ ПАРЫ
 * РЕШЕНИЯ (18976, Р73, пятый заход: пара переехала из потока `RowBody` в
 * абсолютный угол — то же требование заказчика, что и у иконок, «данные
 * лучше выровнять посередине по вертикали»: пока пара стояла в потоке
 * `RowBody`, шапка `compact` прижималась к ВЕРХУ растянутой парой строки, а не
 * центрировалась). Инсет — ТОТ ЖЕ `theme.spacing(2)`, что несёт паддинг корня
 * и верхний угол `trailing` (`compact` паддинг корня не растёт, см.
 * `getRootSx`) — те же четыре угла карточки, один и тот же отступ.
 *
 * ТОЛЬКО `compact`: у `list` пара решения остаётся В ПОТОКЕ, на одной линии с
 * рядом полей (`LIST_FIELDS_SX`, заказчик прямо потребовал «не в футере, а
 * параллельно данным») — см. `DECISION_SLOT_SX`.
 * @param theme - тема MUI
 */
export const getDecisionCornerSx = (theme: Theme): SxProps<Theme> => ({
  position: 'absolute',
  zIndex: 2,
  bottom: theme.spacing(2),
  right: theme.spacing(2)
});

/**
 * Пара решения («Отклонить»/«Принять», `list`, 18976, Р73, четвёртый заход) —
 * НЕ `footer`: заказчик прямо потребовал убрать её из отдельной полосы под
 * строкой и поставить в правый НИЖНИЙ угол, на одной линии с рядом
 * подписанных полей (`LIST_FIELDS_SX`). `compact` (без ряда полей) держит эту
 * же пару АБСОЛЮТНЫМ углом (`getDecisionCornerSx`) — см. её JSDoc.
 *
 * `marginLeft: 'auto'` прижимает элемент к правому краю `LIST_FIELDS_SX`
 * (флекс-ряд на `WIDE_ROW`, флекс-колонка ниже — авто-отступ работает в обоих
 * направлениях одинаково). Правый край при этом совпадает с `trailing` САМ
 * ПО СЕБЕ, без отдельной компенсации: узел лежит внутри `rowBox`
 * (`getRootSx`), чей паддинг — `theme.spacing(2)`/`theme.spacing(3)`, ТЕ ЖЕ
 * вызовы, что несёт инсет `getCornerSx` (18976, Р73, пятый заход — раньше
 * `getCornerSx` нёс сырые пиксели, вдвое больше настоящего паддинга, и здесь
 * стояла компенсирующая `marginRight`; замер её больше не находит нужной).
 */
export const DECISION_SLOT_SX: SxProps<Theme> = { marginLeft: 'auto' };

/**
 * Слот действий СТРОКИ ЗАГОЛОВКА (`titleActions`, 18976, Р3, второй заход):
 * сердце[+папка] актёрской строки. Раньше эта пара плавала абсолютным
 * оверлеем над ВСЕЙ строкой (`UserCardCornerActions`, `position: absolute`),
 * не резервируя ширину нигде, — из-за этого длинное имя тянулось на всю
 * ширину колонки и уезжало буквами ПОД иконки вместо обрезки многоточием
 * (замер `getBoundingClientRect` на ФИО с дефисами, воспроизводилось на
 * 375/900). Фикс — сделать пару РЕАЛЬНЫМ флекс-соседом, но ТОЛЬКО строки
 * заголовка (`title`/`compact`-шапки), а не всей текстовой колонки: поля под
 * именем (`list`) и так не должны получать этот резерв — именно ради этого
 * прошлая волна убрала фиксированные 88px из колонки целиком. `flexShrink: 0`
 * — блок фиксированного размера (тач-цели 44×44), сжимается имя/мета рядом с
 * ним, а не он сам.
 */
export const TITLE_ACTIONS_SX: SxProps<Theme> = { flexShrink: 0 };

/** Заголовок `list` рядом с `titleActions` — реальный флекс-ряд вместо оверлея */
export const TITLE_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  minWidth: 0
};

/** Сам заголовок внутри `TITLE_ROW_SX`: занимает всё, кроме `titleActions` */
export const TITLE_SLOT_SX: SxProps<Theme> = { flex: 1, minWidth: 0 };

/**
 * Ниже доменного порога (`compactHeadWrap`, 18976, Р8, второй заход — кастинг)
 * мета-строка компактного вида переносится на вторую линию вместо обрезки до
 * двух букв («Ак…» на месте роли на 375px, где имя+мета+три иконки не влезали
 * в одну полосу). Каркас сам не переносит — это дефолт `flexWrap: 'nowrap'`
 * головы (см. её `sx` ниже), включается ТОЛЬКО когда домен явно объявил, что
 * его мета-строка умеет перенестись: сама точка переноса — `flexBasis: '100%'`
 * НА ПОЛЕ `meta`, который домен ставит своим контейнерным порогом (см.
 * `CastingListRow#COMPACT_META_SX`), а не решение каркаса угадать порог за
 * домен.
 *
 * `as const` вместо аннотации `SxProps<Theme>`: единственная константа файла,
 * которую потребитель подставляет УСЛОВНО элементом массива `sx`
 * (`cond && COMPACT_HEAD_WRAP_SX`, см. `RowHeader`). Элемент массива `sx` —
 * это `SystemStyleObject` — тип строго уже `SxProps` (последний включает в себя
 * и сам массив), и через границу модуля TS это различие уже не прощает.
 */
export const COMPACT_HEAD_WRAP_SX = { flexWrap: 'wrap', rowGap: 1 } as const;

/**
 * Голова `compact` (заголовок + `meta` + плашка) — прямой ребёнок колонки тела
 * строки, ширину берёт растяжением колонки, а не `flex`-ростом (волна 4 19243
 * вешала `flex: 1 1 auto`, он действовал по вертикали колонки)
 */
export const COMPACT_HEAD_SX = {
  minWidth: 0,
  overflow: 'hidden',
  flexWrap: 'nowrap'
} as const;

/**
 * Первая строка головы `compact` с резервом угла (`CompactRowHead`): заголовок
 * [+ `meta` домена без порога] + резерв, неразрывно. `flex: 1 1 auto` — строка
 * занимает всю ширину головы, а длинный заголовок сжимается ВНУТРИ неё
 * (`minWidth: 0`), не выталкивая резерв на следующую строку переноса
 */
export const COMPACT_LINE_ONE_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  flex: '1 1 auto',
  minWidth: 0
} as const;

/**
 * Плашка статуса компактной головы (18976, Р56): раньше `badges` рисовался
 * `RowBody` ОТДЕЛЬНЫМ рядом под шапкой (`title`+`meta`) — в `compact`, где
 * высота строки — единственная экономия, лишний ряд удваивал высоту на пустом
 * месте («куча пустого места» под заголовком). Теперь плашка — флекс-сосед
 * `title`/`meta` в ТОЙ ЖЕ голове: на широкой строке всё в одну линию (заказчик
 * 17.09.2026: «плашка статуса, затем кнопки, всё в одной строке»), на узкой —
 * `flexBasis: '100%'` уводит ЕЁ ОДНУ на вторую строку (она последняя в
 * флекс-порядке головы, поэтому перенос не утягивает за собой ничего после
 * неё). Настоящие кнопки (`trailing`) в голову не входят вовсе — это внешний
 * флекс-сосед всей строки, а не головы, и остаются на месте независимо от
 * переноса плашки.
 */
export const COMPACT_BADGES_SX: SxProps<Theme> = {
  display: 'flex',
  flexShrink: 0,
  flexBasis: '100%',
  [WIDE_ROW]: { flexBasis: 'auto' }
};

/**
 * Обёртка, которую `ListRowFrame` заводит БЕЗУСЛОВНО (18976, Р66 — до этой
 * правки заводилась только когда домен передавал `footer`): единственный
 * узел, объявляющий контейнерный контекст (`containerType`/`containerName`)
 * НАД строкой (и футером под ней, когда он есть).
 *
 * До правки контейнер объявлял корень строки (`getRootSx`) САМ на себе. Это
 * работало для роста медиа-слота и кегля поля — их стили лежат на ДЕТЯХ
 * корня, обычных потомках контейнера. Но `[WIDE_ROW]`-паддинг того же
 * `getRootSx` лежит на САМОМ корне — том же элементе, что объявляет
 * контейнмент, — а CSS не разрешает элементу резолвить `@container` по
 * собственному объявлению (самоссылка, спецификация её игнорирует). Когда
 * домен передавал `footer`, эта обёртка уже существовала СНАРУЖИ корня и
 * случайно чинила самоссылку, давая корню внешнего тёзку-предка; без
 * `footer` корень был единственным носителем имени в дереве, и паддинг
 * никогда не бампался, независимо от реальной ширины (замер
 * `L-current-state-measurements.md`, раздел 2, «Побочная находка»).
 *
 * Обёртка теперь единственный носитель контейнмента всегда — `getRootSx`
 * контейнер больше не объявляет (см. его JSDoc), самоссылки не возникает ни
 * в одном из двух случаев (с `footer` и без). Она же несёт поверхность
 * (`surface`), когда домен не берёт её на себя: раньше поверхность стояла на
 * корне заодно с контейнментом, теперь и то, и другое — снаружи, на одном узле.
 *
 * `position: relative` (18976, Р73): единственный якорь для абсолютного
 * `trailing`-угла (см. `getCornerSx`) — расстояние до края строки читается от
 * НЕЁ, а не от домена, который может (`CastingListRow`, `ProjectListRow`) или
 * не может (`ActorCardListRow`) держать собственную обёртку снаружи.
 */
export const ROW_CONTAINER_SX: SxProps<Theme> = {
  width: '100%',
  position: 'relative',
  containerType: 'inline-size',
  containerName: ROW_CONTAINER
};
